/**
 * The per-row actions menu (brief §17: "Do NOT put four large buttons into
 * every row" - one compact three-dot menu instead).
 *
 * Rendered at page level and positioned against the button's viewport rect,
 * NOT absolutely inside the cell: the grid is an `overflow: auto` container, so
 * a dropdown positioned within it would be clipped by the very scrolling that
 * makes the grid usable.
 *
 * The brief's §17 sketch also lists "Generate Invoice" and "Print Invoice" per
 * room. Those are deliberately absent. FRD §8 is unambiguous that "the invoice
 * / amount-due relationship is always at the Client level. Room-wise amounts
 * are supporting detail only, not separate bills" - and the schema enforces it
 * (one invoice per billing cycle). Offering a per-room invoice would promise
 * something the business does not do.
 */
import { useEffect, useRef } from 'react'

import { useColors } from '../../../hooks/useColors'

export interface RowActionsMenuProps {
  roomNumber: string
  anchor: DOMRect
  onClose: () => void
  onEditBilling: () => void
  onPrintTags: () => void
  /** The A5 sorting sheet. Never called an invoice - FRD §8 has only one. */
  onPrintRoomSlip: () => void
  onViewServices: () => void
  /** Tags describe what is on the server, so an unsaved room cannot print. */
  canPrintTags: boolean
}

const MENU_WIDTH = 172

export default function RowActionsMenu({
  roomNumber,
  anchor,
  onClose,
  onEditBilling,
  onPrintTags,
  onPrintRoomSlip,
  onViewServices,
  canPrintTags,
}: RowActionsMenuProps) {
  const colors = useColors()
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) onClose()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    // Scrolling the grid moves the row out from under a fixed menu, so close
    // rather than leave it pointing at the wrong room.
    const onScroll = () => onClose()

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    window.addEventListener('scroll', onScroll, true)
    ref.current?.focus()

    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [onClose])

  const items: Array<{ label: string; onClick: () => void; disabled?: boolean; title?: string }> = [
    { label: 'Edit billing', onClick: onEditBilling },
    {
      label: 'Print tags',
      onClick: onPrintTags,
      disabled: !canPrintTags,
      title: canPrintTags ? undefined : 'Save this room before printing its tags',
    },
    {
      label: 'Print room slip',
      onClick: onPrintRoomSlip,
      disabled: !canPrintTags,
      title: canPrintTags ? undefined : 'Save this room before printing its slip',
    },
    { label: 'View room services', onClick: onViewServices },
  ]

  return (
    <div
      ref={ref}
      role="menu"
      tabIndex={-1}
      aria-label={`Actions for room ${roomNumber}`}
      style={{
        position: 'fixed',
        // Right-aligned under the button, clamped so it never leaves the window.
        top: `${Math.min(anchor.bottom + 4, window.innerHeight - 140)}px`,
        left: `${Math.max(8, anchor.right - MENU_WIDTH)}px`,
        width: `${MENU_WIDTH}px`,
        zIndex: 70,
        padding: '4px',
        borderRadius: '10px',
        border: `1px solid ${colors.border}`,
        background: colors.bgCard,
        boxShadow: '0 8px 20px rgba(15,46,42,0.16)',
        outline: 'none',
      }}
    >
      <div
        className="font-body"
        style={{
          padding: '6px 8px 4px',
          fontSize: '10px',
          fontWeight: 700,
          letterSpacing: '0.7px',
          textTransform: 'uppercase',
          color: colors.textMuted,
        }}
      >
        Room {roomNumber}
      </div>
      {items.map((item) => (
        <button
          key={item.label}
          type="button"
          role="menuitem"
          disabled={item.disabled}
          title={item.title}
          onClick={() => {
            item.onClick()
            onClose()
          }}
          className="font-body"
          style={{
            display: 'block',
            width: '100%',
            textAlign: 'left',
            padding: '7px 8px',
            borderRadius: '6px',
            border: 'none',
            background: 'transparent',
            fontSize: '12px',
            fontWeight: 600,
            color: item.disabled ? colors.textMuted : colors.textPrimary,
            cursor: item.disabled ? 'not-allowed' : 'pointer',
          }}
        >
          {item.label}
        </button>
      ))}
    </div>
  )
}
